import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';

import * as S from './styled';
import CommonButton from '../../../../components/UI/button';
import { GET_ALL_SITE_TARIFFS, INSERT_NEW_TARIFF } from '../../../../apollo/settings';
import { validateTariffValues } from '../../../../utils';
import { useContextInfo } from '../../../../context';
import { validateWholeNumbers } from '../../../../utils/validators';

const GeneralSettings = () => {
  const { t } = useTranslation();
  const { selectedSite, setSelectedSite } = useContextInfo();

  const [siteOptions, setSiteOptions] = useState(null);
  const [values, setValues] = useState([]);

  const { data: tariffSiteData, loading: tariffLoading } = useQuery(GET_ALL_SITE_TARIFFS, {
    variables: {
      newestValidTariffOnly: true
    }
  });

  const [insertNewTariff, { loading: insertLoading }] = useMutation(INSERT_NEW_TARIFF, {
    refetchQueries: [
      {
        query: GET_ALL_SITE_TARIFFS,
        variables: { newestValidTariffOnly: true }
      }
    ],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (tariffSiteData?.allSiteTariffs) {
      const options = tariffSiteData.allSiteTariffs.map((item) => ({
        id: item.id,
        value: item.id,
        name: item.name
      }));

      setSiteOptions(options);

      if (!selectedSite) {
        setSelectedSite(options[0]?.value);
      }
    }
  }, [tariffSiteData]);

  // 🔥 Wichtig: alte Formwerte beim Sitewechsel löschen
  useEffect(() => {
    setValues([]);
  }, [selectedSite]);

  const handlerSelect = (e) => {
    const currentValue = siteOptions?.find((item) => item.id === e.target.value);
    setSelectedSite(currentValue?.value);
  };

  const handlerInputData = (e, tariff_key, group_id) => {
    const currentValues = [...values];
    const hasGroupId = currentValues.some((item) => item.group_id === group_id);

    const value = e.target.value ? fromGermanFormat(e.target.value) : e.target.value;

    if (hasGroupId) {
      const updatedValues = currentValues.map((group) => {
        if (group.group_id === group_id) {
          return { ...group, [tariff_key]: value };
        }
        return group;
      });
      setValues(updatedValues);
    } else {
      setValues([{ group_id, [tariff_key]: value }, ...currentValues]);
    }
  };

  const handlerSubmit = async (group_id, oldValues) => {
    if (!values.length) return;

    const defaults = oldValues ?? {
      free_period_in_minutes: null,
      max_block_price: null,
      price_per_kwh: null,
      price_per_minute: null,
      monthly_base_fee: null,
      monthly_card_fee: null
    };

    const updateValues = values.find((v) => v.group_id === group_id);

    await insertNewTariff({
      variables: {
        tariff: {
          group_id,
          site_id: selectedSite,
          free_period_in_minutes:
            updateValues?.free_period_in_minutes ?? defaults.free_period_in_minutes,
          max_block_price: updateValues?.max_block_price ?? defaults.max_block_price,
          price_per_kwh: updateValues?.price_per_kwh ?? defaults.price_per_kwh,
          price_per_minute: updateValues?.price_per_minute ?? defaults.price_per_minute,
          monthly_base_fee: updateValues?.monthly_base_fee ?? defaults.monthly_base_fee,
          monthly_card_fee: updateValues?.monthly_card_fee ?? defaults.monthly_card_fee
        }
      }
    });

    setValues([]);
  };

  const getCurrentTariff = (tariffs) => {
    const now = new Date(Date.now() + 1000);
    return tariffs.reduce((latest, current) => {
      const c = new Date(current.beginn_ts);
      const l = new Date(latest.beginn_ts);
      return c <= now && c > l ? current : latest;
    }, tariffs[0]);
  };

  const toGermanFormat = (num) =>
    num ? num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : num;

  const fromGermanFormat = (str) =>
    str ? parseFloat(str.replace('.', '').replace(',', '.')) : str;

  if (tariffLoading || insertLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <S.Title>{t('settings.select_site')}</S.Title>

      {siteOptions && (
        <S.SelectSiteStyle options={siteOptions} onChange={handlerSelect} value={selectedSite} />
      )}

      {/* 🔥 Dieser Key erzwingt komplettes Remounten bei Sitewechsel */}
      <div key={selectedSite}>
        {tariffSiteData?.allSiteTariffs
          ?.filter((site) => site.id === selectedSite)
          .map((site) =>
            site.groups.map((group) => {
              const currentTariff = group.tariffs?.length ? getCurrentTariff(group.tariffs) : null;

              return (
                <S.Block key={`${selectedSite}_${group.group_id}`}>
                  <S.SubTitle>
                    {t('settings.group_tariff')} {group.group_name}
                  </S.SubTitle>

                  <S.InputBlock>
                    <S.SettingInput
                      label={t('settings.pricePerkWh')}
                      defaultValue={toGermanFormat(currentTariff?.price_per_kwh)}
                      onInput={validateTariffValues}
                      onChange={(e) => handlerInputData(e, 'price_per_kwh', group.group_id)}
                    />
                    <S.SettingInput
                      label={t('settings.pricePerMinute')}
                      defaultValue={toGermanFormat(currentTariff?.price_per_minute)}
                      onInput={validateTariffValues}
                      onChange={(e) => handlerInputData(e, 'price_per_minute', group.group_id)}
                    />
                    <S.SettingInput
                      label={t('settings.freePeriodInMinutes')}
                      defaultValue={currentTariff?.free_period_in_minutes}
                      onInput={validateWholeNumbers}
                      onChange={(e) =>
                        handlerInputData(e, 'free_period_in_minutes', group.group_id)
                      }
                    />
                    <S.SettingInput
                      label={t('settings.maxBlockPrice')}
                      defaultValue={toGermanFormat(currentTariff?.max_block_price)}
                      onInput={validateTariffValues}
                      onChange={(e) => handlerInputData(e, 'max_block_price', group.group_id)}
                    />
                    <S.SettingInput
                      label={t('settings.monthlyBaseFee')}
                      defaultValue={toGermanFormat(currentTariff?.monthly_base_fee)}
                      onInput={validateTariffValues}
                      onChange={(e) => handlerInputData(e, 'monthly_base_fee', group.group_id)}
                    />
                    <S.SettingInput
                      label={t('settings.monthlyCardFee')}
                      defaultValue={toGermanFormat(currentTariff?.monthly_card_fee)}
                      onInput={validateTariffValues}
                      onChange={(e) => handlerInputData(e, 'monthly_card_fee', group.group_id)}
                    />
                  </S.InputBlock>

                  <CommonButton
                    text={t('settings.submit')}
                    onClick={() => handlerSubmit(group.group_id, currentTariff)}
                  />
                </S.Block>
              );
            })
          )}
      </div>
    </>
  );
};

export default GeneralSettings;
